import {
  Button,
  Descriptions,
  Divider,
  Modal,
  Popconfirm,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import React from "react";
import { useSelector } from "react-redux";
import { IExportTransfer } from "../../../models/Warehouse";
import { RootState } from "../../../store";
import { useUpdateWarehouseExportTransfer } from "../../../query/WarehouseQuery";
import { notify } from "../../../utils/toast";
import { IWarehouseLocation } from "../../../models/Warehouse";

const { Text, Title } = Typography;

interface ModalViewExportTransferProps {
  open: boolean;
  selectedItems: IExportTransfer;
  onOk: () => void;
  onCancel: () => void;
}

const ModalViewExportTransfer: React.FC<ModalViewExportTransferProps> = ({
  open,
  selectedItems,
  onOk,
  onCancel,
}) => {
  const locations = useSelector(
    (state: RootState) => state.warehouse.locations,
  );
  const updateStatusMutation = useUpdateWarehouseExportTransfer();

  if (!selectedItems) return null;
  const item = Array.isArray(selectedItems) ? selectedItems[0] : selectedItems;

  const handleUpdateStatus = async () => {
    try {
      const res = await updateStatusMutation.mutateAsync({
        id: item.id,
        status: true,
      });
      notify.success("Chuyển kho thành công");
      item.isCompleted = res.status;
    } catch (error: any) {
      if (error.isAxiosError) {
        const serverMessage = error.response?.data?.message;
        notify.error(serverMessage);
      }
    }
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Mã hàng",
      dataIndex: ["partNumberCurrent", "partNumber"],
      key: "partNumber",
      align: "center",
      width: 180,
    },
    {
      title: "Từ",
      key: "fromLocation",
      children: [
        {
          title: "Dự án",
          dataIndex: ["partNumberCurrent", "project"],
          align: "center",
        },
        {
          title: "Mã dự án",
          dataIndex: ["partNumberCurrent", "projectCode"],
          align: "center",
        },
        {
          title: "Mã kho",
          dataIndex: ["currentLocation", "warehouseId"],
          align: "center",
          render: (warehouseId: number) => {
            const location = locations.find(
              (loc: IWarehouseLocation) => loc.id === warehouseId,
            );
            return location ? (
              <Text>{location.warehouseCode}</Text>
            ) : (
              <Text type="secondary">Unknown</Text>
            );
          },
        },
        {
          title: "Vị trí",
          dataIndex: ["currentLocation", "code"],
          align: "center",
        },
      ],
    },
    {
      title: "Đến",
      key: "toLocation",
      children: [
        {
          title: "Dự án",
          dataIndex: ["targetProject"],
          align: "center",
        },
        {
          title: "Mã dự án",
          dataIndex: ["targetProjectCode"],
          align: "center",
        },
        {
          title: "Mã kho",
          dataIndex: ["targetLocation", "warehouseId"],
          align: "center",
          render: (warehouseId: number) => {
            const location = locations.find(
              (loc: IWarehouseLocation) => loc.id === warehouseId,
            );
            return location ? (
              <Text>{location.warehouseCode}</Text>
            ) : (
              <Text type="secondary">Unknown</Text>
            );
          },
        },
        {
          title: "Vị trí",
          dataIndex: ["targetLocation", "code"],
          align: "center",
        },
      ],
    },
    {
      title: "AWB",
      dataIndex: ["partNumberTarget", "awb"],
      key: "awb",
      align: "center",
    },
    {
      title: "Số lượng chuyển",
      dataIndex: "exportQuantity",
      key: "exportQuantity",
      align: "center",
    },
  ];

  return (
    <Modal
      title="Chi tiết phiếu chuyển kho"
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      width={1200}
      height={800}
      footer={null}
    >
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>{item.exportTransferName}</Title>
          <Space>
            <Tag color={item.isCompleted ? "success" : "error"}>
              {item.isCompleted ? "Hoàn tất" : "Đang chờ xác nhận"}
            </Tag>
            <Popconfirm
              title="Xác nhận chuyển kho"
              description={
                <div>
                  <p>Bạn có chắc chắn muốn xác nhận chuyển kho?</p>
                  <p>Hành động này không thể hoàn tác.</p>
                </div>
              }
              onConfirm={handleUpdateStatus}
              okText="Xác nhận"
              cancelText="Hủy"
              disabled={item.isCompleted}
              placement="topRight"
            >
              <Button
                type="primary"
                disabled={item.isCompleted}
                loading={updateStatusMutation.isPending}
              >
                Xác nhận chuyển kho
              </Button>
            </Popconfirm>
          </Space>
        </div>

        <Descriptions bordered column={2}>
          <Descriptions.Item label="Người tạo">
            {item.createdByUser.fullName}
          </Descriptions.Item>
          <Descriptions.Item label="Người nhận" span={2}>
            {item.receiver}
          </Descriptions.Item>
          <Descriptions.Item label="Thời gian tạo">
            {new Date(item.createdAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Cập nhật mới nhất" span={2}>
            {new Date(item.updatedAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Ghi chú" span={2}>
            {item.note || "Không có ghi chú"}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">Danh sách Part Number chuyển kho</Divider>

        <Table
          columns={columns}
          dataSource={item.partNumberExportTransferDetails}
          pagination={false}
          rowKey="id"
          bordered
          size="small"
        />
      </div>
    </Modal>
  );
};

export default ModalViewExportTransfer;
